import { useCallback, useEffect, useState } from 'react'
import { del, get, post } from '../lib/api'

/**
 * The people in the deal, and messages to them.
 *
 * Sending is two steps on purpose: a message is drafted, read back, and sent as
 * a separate act. Writing is reversible; sending is not, and the two should not
 * share a button.
 */

type Contact = {
  id: string; name: string; role: string; org: string | null
  email: string | null; phone: string | null; notes: string | null
}
type Message = {
  id: string; to_address: string; subject: string; body: string
  status: string; provider: string | null; error: string | null; created_at: number; sent_at: number | null
}

const BLANK = { name: '', role: 'broker', org: '', email: '', phone: '', notes: '' }

export default function Parties() {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [roles, setRoles] = useState<string[]>([])
  const [form, setForm] = useState<typeof BLANK & { id?: string }>(BLANK)
  const [adding, setAdding] = useState(false)
  const [writing, setWriting] = useState<Contact | null>(null)
  const [draft, setDraft] = useState({ subject: '', body: '' })
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const r = await get<{ contacts: Contact[]; messages: Message[]; roles: string[] }>('/api/parties')
    setContacts(r.contacts)
    setMessages(r.messages)
    setRoles(r.roles)
  }, [])

  useEffect(() => { void load().catch((e) => setNote(e instanceof Error ? e.message : 'Could not load.')) }, [load])

  async function save() {
    if (!form.name.trim()) return
    setBusy(true)
    try {
      await post('/api/parties', form)
      setForm(BLANK)
      setAdding(false)
      await load()
    } finally { setBusy(false) }
  }

  async function remove(c: Contact) {
    setBusy(true)
    try { await del(`/api/parties/${c.id}`); await load() } finally { setBusy(false) }
  }

  async function saveDraft() {
    if (!writing?.email || !draft.subject.trim() || !draft.body.trim()) return
    setBusy(true)
    try {
      await post('/api/messages', { contactId: writing.id, to: writing.email, subject: draft.subject, body: draft.body })
      setWriting(null)
      setDraft({ subject: '', body: '' })
      setNote('Drafted. Nothing has been sent — press Send on the draft below.')
      await load()
    } finally { setBusy(false) }
  }

  async function send(m: Message) {
    setBusy(true)
    try {
      const r = await post<{ message: Message }>(`/api/messages/${m.id}/send`, {})
      setNote(
        r.message.status === 'sent'
          ? `Sent to ${r.message.to_address}${r.message.provider === 'console' ? ' — but the provider is "console", so it was only logged, not delivered.' : '.'}`
          : `Could not send: ${r.message.error ?? 'unknown error'}`,
      )
      await load()
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Send failed.')
    } finally { setBusy(false) }
  }

  return (
    <div className="parties">
      {note && <p className="cc-note" onClick={() => setNote(null)}>{note}</p>}

      <div className="parties-head">
        <h3>Who is in this deal</h3>
        <button type="button" className="btn-s btn-go" onClick={() => { setAdding(!adding); setForm(BLANK) }}>
          {adding ? 'Cancel' : 'Add someone'}
        </button>
      </div>

      {adding && (
        <div className="party-form">
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {(roles.length ? roles : ['broker']).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input placeholder="Company" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} />
          <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <button type="button" className="btn-s btn-go" disabled={busy || !form.name.trim()} onClick={() => void save()}>Save</button>
        </div>
      )}

      {!contacts.length && !adding && <p className="muted">Nobody yet. Add the broker, the loan officer, the CPA and counsel.</p>}

      {contacts.length > 0 && (
        <div className="party-grid">
          {contacts.map((c) => (
            <div key={c.id} className="party">
              <div className="party-top">
                <strong>{c.name}</strong>
                <span className="party-role">{c.role}</span>
              </div>
              {c.org && <div className="cc-sub">{c.org}</div>}
              {c.email && <a href={`mailto:${c.email}`} className="party-link">{c.email}</a>}
              {c.phone && <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`} className="party-link">{c.phone}</a>}
              {c.notes && <div className="cc-sub">{c.notes}</div>}
              <div className="cc-actions">
                <button
                  type="button"
                  className="btn-s"
                  disabled={!c.email}
                  title={c.email ? undefined : 'No email address on this contact'}
                  onClick={() => { setWriting(c); setDraft({ subject: '', body: '' }) }}
                >
                  Write
                </button>
                <button type="button" className="btn-s" onClick={() => void remove(c)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {writing && (
        <div className="party-compose">
          <h3>To {writing.name} &lt;{writing.email}&gt;</h3>
          <input placeholder="Subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          <textarea rows={8} placeholder="Write it in your own words." value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          <div className="cc-actions">
            <button type="button" className="btn-s btn-go" disabled={busy || !draft.subject.trim() || !draft.body.trim()} onClick={() => void saveDraft()}>
              Save as draft
            </button>
            <button type="button" className="btn-s" onClick={() => setWriting(null)}>Cancel</button>
          </div>
          <p className="cc-sub">Saving does not send. Sending is a separate press, on the draft.</p>
        </div>
      )}

      {messages.length > 0 && (
        <>
          <h3>Messages</h3>
          <table className="cc-table">
            <thead><tr><th>To</th><th>Subject</th><th>Status</th><th /></tr></thead>
            <tbody>
              {messages.map((m) => (
                <tr key={m.id} className={m.status === 'failed' ? 'overdue' : ''}>
                  <td>{m.to_address}</td>
                  <td><strong>{m.subject}</strong><span className="cc-sub">{m.body.slice(0, 90)}…</span></td>
                  <td>
                    <span className={`cc-badge ${m.status === 'sent' ? 'ok' : 'bad'}`}>{m.status}</span>
                    {m.error && <span className="cc-sub">{m.error}</span>}
                  </td>
                  <td>
                    {m.status === 'draft' && (
                      <button type="button" className="btn-s btn-go" disabled={busy} onClick={() => void send(m)}>Send</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
